import { constructMSAImageUrl } from 'helpers/index';
import { buildRecosKey, RecoSlotDetails } from 'helpers/RecoUtils';
import { toUSD } from 'helpers/NumberFormats';
import { HEART_DEFAULT_LIST_ID } from 'constants/apis';
import { INFLUENCER_NEW_COLLECTION_DAYS } from 'constants/influencerPages';
import { HeartProductDetailProps, HeartProps, UnHeartProductDetailProps } from 'types/hearts';

interface GetHeartPropsParams {
  heartProduct: (heartProps: HeartProductDetailProps) => void;
  unHeartProduct: (productId: UnHeartProductDetailProps) => void;
  toggleHeartingLoginModal: (show: boolean, styleId: string) => void;
  trackEvent: (eventName: string, eventData?: string) => void;
  hasHearting?: boolean;
  isCustomer?: boolean;
  hearts?: string[];
  products?: Record<string, any> /** @todo - add types to products values */;
  isDisplayCount?: boolean;
  hydraHeartsStars?: boolean;
}

type MakeHandlerHeartButtonClickParams = {
  hearts: string[];
  onHeartClick: (params: { styleId: string; productId: string; price: number }, isHearted: boolean) => void;
  onCollectionToggle: ((show: boolean) => void) | undefined;
  productId: string;
  showFavoriteHeart: boolean;
  style: {
    styleId: string;
    basePrice: number;
    salePrice: number;
  };
};

export type HeartListOrCollection = {
  listId: string;
  shareToken: string;
  createTime: number;
  updateTime: number;
  itemCount: number;
  metadata: {
    published: boolean;
    headerLayout: number;
    images: { approved: boolean; feedback: unknown }[];
  };
};

export interface HeartEventData {
  heartEventName: string;
  unHeartEventName: string;
}

export function makeHeartClickHandler(heartEventData: HeartEventData, clickHandlerDataAndActions: GetHeartPropsParams) {
  const { heartEventName, unHeartEventName } = heartEventData;
  const { heartProduct, unHeartProduct, toggleHeartingLoginModal, trackEvent, isCustomer } = clickHandlerDataAndActions;
  return function ({ styleId }: { styleId: string }, isHearted: boolean) {
    if (isCustomer) {
      if (isHearted) {
        unHeartProduct({ itemId: styleId } as UnHeartProductDetailProps);
        trackEvent(unHeartEventName, styleId);
      } else {
        heartProduct({ itemId: styleId } as HeartProductDetailProps);
        trackEvent(heartEventName, styleId);
      }
    } else {
      toggleHeartingLoginModal(true, styleId);
    }
  };
}

export function isProductHearted(showFavoriteHeart: boolean, hearts: string[] = [], styleId: string) {
  return !!(showFavoriteHeart && styleId && hearts?.indexOf(styleId) > -1);
}

export function makeHandleHeartButtonClick({
  hearts,
  onHeartClick,
  onCollectionToggle,
  productId,
  showFavoriteHeart,
  style
}: MakeHandlerHeartButtonClickParams) {
  return (e: Event) => {
    const { styleId, basePrice, salePrice } = style;
    const price = basePrice !== salePrice ? salePrice : basePrice;
    const isHearted = isProductHearted(showFavoriteHeart, hearts, styleId);

    // Prevent incorrect tracking events and going to product url
    e.stopPropagation();
    e.preventDefault();
    // Run hearting callback if present
    if (onHeartClick) {
      onCollectionToggle && onCollectionToggle(!isHearted); // show collection if product has not been hearted prior to clicking "Heart"
      onHeartClick(
        {
          styleId,
          productId,
          price
        },
        isHearted
      );
    }
  };
}

// combines list object information with the items
export function makeCollectionInformationWithItems(lists: HeartListOrCollection[], collections: HeartListOrCollection[]) {
  const collectionWithItems: HeartListOrCollection[] = [];
  if (lists?.length && collections?.length) {
    lists.forEach(list => {
      const correspondingCollection = collections.find(collection => list.listId === collection.listId);
      collectionWithItems.push({
        ...list,
        ...correspondingCollection
      });
    });
  }

  return collectionWithItems;
}

export function makePublishedCollectionInformationWithItems(lists: HeartListOrCollection[], collections: HeartListOrCollection[]) {
  const collectionWithItems: HeartListOrCollection[] = [];
  if (lists?.length && collections?.length) {
    lists.forEach(list => {
      const correspondingPublishedCollection = collections.find(collection => list.shareToken === collection.shareToken);
      collectionWithItems.push({
        ...list,
        ...correspondingPublishedCollection
      });
    });
  }
  return collectionWithItems;
}

export function sortInfluencerCollections(collections: HeartListOrCollection[]) {
  const nDays = INFLUENCER_NEW_COLLECTION_DAYS;
  const nDaysAgo = new Date();
  nDaysAgo.setDate(nDaysAgo.getDate() - nDays);

  function filterOnCreateDate(collection: HeartListOrCollection) {
    return collection.createTime >= nDaysAgo.getTime();
  }

  const newCollectionList = collections?.filter(collection => filterOnCreateDate(collection));
  const collectionsToSort = collections?.slice(newCollectionList.length, collections?.length);
  const sortedCollectionList = collectionsToSort.sort((a, b) => b.updateTime - a.updateTime);
  const listToDisplay = newCollectionList.concat(sortedCollectionList);
  return listToDisplay;
}

export function getHeartProps(heartProps: GetHeartPropsParams, { heartEventName, unHeartEventName }: HeartEventData) {
  const {
    hasHearting,
    isCustomer,
    heartProduct,
    hearts,
    products = {},
    toggleHeartingLoginModal,
    trackEvent,
    unHeartProduct,
    isDisplayCount,
    hydraHeartsStars
  } = heartProps || {};
  if (hasHearting) {
    const clickHandlerDataAndActions = {
      heartProduct,
      isCustomer,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct
    };
    return {
      showFavoriteHeart: true,
      onHeartClick: makeHeartClickHandler({ heartEventName, unHeartEventName }, clickHandlerDataAndActions),
      hearts,
      heartsList: products.heartsList,
      isDisplayCount,
      hydraHeartsStars
    };
  }

  return { showFavoriteHeart: false };
}

export function getHeartingStyleIdsForComponent(
  slotData: {
    [key: string]: {
      componentName: string;
      trending?: {
        results?: { styleId: string }[];
      };
      products?: { styleId: string }[];
    } & RecoSlotDetails;
  } = {},
  recommenderEntries: {
    janus?: {
      [key: string]: {
        recos?: { styleId: string }[];
      };
    };
  } = {}
) {
  const results: { styleId: string }[] = [];
  Object.values(slotData).forEach(slot => {
    switch (slot.componentName) {
      case 'genericBrandTrending':
        results.push(...(slot.trending?.results || []));
        break;
      case 'productSearch':
        results.push(...(slot.products || []));
        break;
      case 'melodyGrid':
        results.push(...(slot.products || []));
        break;
      case 'recommender':
        const { janus = {} } = recommenderEntries;
        const recosKey = buildRecosKey(slot);
        const { recos } = janus[recosKey] || {};
        if (recos?.length && recos?.length > 0) {
          results.push(...recos);
        }
        break;
    }
  });
  return results.map(result => ({ styleId: result.styleId })).filter(result => !!result);
}

export function formatHeartsForMelodyCard(heart: { imageId: string; price: number | string }) {
  const { imageId, price } = heart;
  return {
    ...heart,
    thumbnailImageId: imageId,
    thumbnailImageUrl: constructMSAImageUrl(imageId, {
      autoCrop: true,
      width: 272
    }),
    price: typeof price === 'number' ? toUSD(price) : price
  };
}

export const formatHeartsForSlots = (product: { productId?: string; styleId: string }, heartsData: HeartProps) => {
  const { productId, styleId } = product;
  const { heartsList } = heartsData;
  const displayHeartCount = heartsList ? true : false;

  const heartsInfo = {
    heartsData: { ...heartsData, isDisplayCount: displayHeartCount },
    styleId,
    productId
  };

  return heartsInfo;
};

export const isDefaultHeartsPresent = (lists: { listId: string }[]) => lists.some(({ listId }) => listId === HEART_DEFAULT_LIST_ID);
